/**
 * CREDIT-1 · 同行额度与应收（契约 = 样稿 credit-mockup-v1 ①–⑤；后端 routes/partner_receivables.py）。
 *
 * 路径前缀是 /api/v1/channel-partners（样稿接口条里的 /b2b/partners 是示意）。
 * 所有数字都由后端算（额度口径唯一在 services/partner_ar.py）；前端只展示 + 拼装请求，不自己算额度 / 到期日。
 */
import { api, apiConfig } from "./api";
import { useAuthStore } from "@/store/authStore";
import type { SettlementMode } from "./b2b";

const BASE = "/api/v1/channel-partners";

/** 结算条件：null = 用全局默认；effective = 实际生效值；global_defaults = 「系统设置 · 通用设置」里的默认 */
export interface SettlementTerms {
  mode: SettlementMode;
  monthly_due_day: number | null;
  per_order_due_days: number | null;
  block_after_overdue_days: number | null;
  effective: { monthly_due_day: number; per_order_due_days: number; block_after_overdue_days: number };
  global_defaults: { monthly_due_day: number; per_order_due_days: number; block_after_overdue_days: number };
}

export interface TempLimit { amount_cents: number; effective_to: string }

export interface CreditOverview {
  limit_cents: number;
  base_limit_cents: number;
  temp_limits: TempLimit[];
  used_cents: number;
  receivable_open_cents: number;
  hold_cents: number;
  available_cents: number;
  overdue_cents: number;
  overdue_count: number;
  overdue_max_days: number;
  prepaid_balance_cents: number;
  blocked: boolean;
  blocked_reason: string | null;
  unblocked_until: string | null;
  terms: SettlementTerms;
}

export interface SettlementTermsPatch {
  mode?: SettlementMode;
  monthly_due_day?: number | null;
  per_order_due_days?: number | null;
  block_after_overdue_days?: number | null;
}

export type ReceivableFilter = "open" | "overdue" | "paid" | "void" | "all";
export type ReceivableStatus = "pending" | "partial" | "paid" | "void";

export interface ReceivableRow {
  id: string;
  receivable_number: string;
  origin: string;
  order_id: string | null;
  order_no: string | null;
  partner_id: string;
  partner_name: string | null;
  product_name: string | null;
  pax_count: number | null;
  travel_date: string | null;
  amount_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  due_date: string;
  issue_date: string;
  status: ReceivableStatus;
  overdue: boolean;
  overdue_days: number;
  statement_id: string | null;
  void_reason: string | null;
}

export interface ReceivableList {
  rows: ReceivableRow[];
  total: number;
  counts: Record<ReceivableFilter, number>;
  open_cents: number;
  overdue_cents: number;
}

export type PaymentMethod = "wire" | "zelle" | "check" | "cash";
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = { wire: "电汇", zelle: "Zelle", check: "支票", cash: "现金" };

export interface PartnerPaymentRow {
  id: string;
  partner_id: string;
  amount_cents: number;
  received_on: string;
  method: PaymentMethod;
  reference: string | null;
  prepaid_used_cents: number;
  prepaid_added_cents: number;
  created_at: string | null;
  voided: boolean;
  voided_at: string | null;
  void_reason: string | null;
}

export interface PaymentInput {
  amount_cents: number;
  received_on: string;
  method: PaymentMethod;
  reference?: string;
  /** 不传 = 后端自动按到期日从早到晚抵扣 */
  allocations?: { receivable_id: string; amount_cents: number }[];
  use_prepaid?: boolean;
}

export interface PaymentResult {
  payment: PartnerPaymentRow;
  allocations: { receivable_id: string; amount_cents: number }[];
  prepaid_used_cents: number;
  prepaid_added_cents: number;
  credit: CreditOverview;
}

export type StatementStatus = "draft" | "sent" | "paid" | "overdue";

export interface StatementLine {
  receivable_id: string;
  order_id: string;
  order_no: string;
  partner_ref: string | null;
  product_name: string | null;
  travel_date: string;
  pax_count: number;
  amount_cents: number;
  paid_cents?: number;
  voided?: boolean;
}

export interface PartnerStatement {
  id: string;
  number: string;
  partner_id: string;
  partner_name: string | null;
  period: string; // YYYY-MM
  period_start: string;
  period_end: string;
  orders_count: number;
  total_cents: number;
  paid_cents: number;
  unpaid_cents: number;
  due_date: string | null;
  overdue: boolean;
  status: StatementStatus;
  sent_at: string | null;
  last_sent_to: string | null;
  generated_by: "auto" | "manual";
  lines?: StatementLine[];
}

export interface StatementSendResult {
  sent_to: string[];
  failed: string[];
  statement: PartnerStatement;
}

/** 登录态下取 HTML / CSV（私有文件，不能直接开链接）→ objectURL */
async function fetchBlob(path: string): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${apiConfig.base}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`加载失败（${res.status}）`);
  return res.blob();
}

export const partnerArApi = {
  credit: (pid: string) => api<CreditOverview>(`${BASE}/${pid}/credit`),
  patchTerms: (pid: string, body: SettlementTermsPatch) => api<CreditOverview>(`${BASE}/${pid}/settlement-terms`, { method: "PATCH", body: JSON.stringify(body) }),
  unblock: (pid: string, reason: string) => api<CreditOverview>(`${BASE}/${pid}:unblock`, { method: "POST", body: JSON.stringify({ reason }) }),

  receivables: (pid: string, status: ReceivableFilter) => api<ReceivableList>(`${BASE}/${pid}/receivables?status=${status}&limit=200`),
  allReceivables: (status: ReceivableFilter) => api<ReceivableList>(`${BASE}/_receivables?status=${status}&limit=200`),

  payments: (pid: string) => api<PartnerPaymentRow[]>(`${BASE}/${pid}/payments`),
  registerPayment: (pid: string, body: PaymentInput) => api<PaymentResult>(`${BASE}/${pid}/payments`, { method: "POST", body: JSON.stringify(body) }),
  // REASON-2 · 作废收款原因必填（payment_void）：reason_code + 说明
  voidPayment: (pid: string, paymentId: string, reason: { reason_code?: string; reason?: string }) =>
    api<{ payment: PartnerPaymentRow; credit: CreditOverview }>(`${BASE}/${pid}/payments/${paymentId}:void`, { method: "POST", body: JSON.stringify(reason) }),

  statements: (pid: string) => api<PartnerStatement[]>(`${BASE}/${pid}/statements`),
  statement: (pid: string, sid: string) => api<PartnerStatement>(`${BASE}/${pid}/statements/${sid}`),
  generateStatement: (pid: string, month?: string) => api<PartnerStatement>(`${BASE}/${pid}/statements:generate`, { method: "POST", body: JSON.stringify(month ? { month } : {}) }),
  sendStatement: (pid: string, sid: string) => api<StatementSendResult>(`${BASE}/${pid}/statements/${sid}:send`, { method: "POST" }),
  sendMonth: (month?: string) =>
    api<{ period: string; statements: number; sent: number; failed: number }>(`${BASE}/_statements:send-month`, { method: "POST", body: JSON.stringify(month ? { month } : {}) }),

  /** 打印 / 存 PDF：打开后端渲染好的打印页（页里自带「打印 / 存 PDF」按钮，浏览器「另存为 PDF」） */
  openStatementPrint: async (pid: string, sid: string): Promise<void> => {
    const blob = await fetchBlob(`${BASE}/${pid}/statements/${sid}/print`);
    window.open(URL.createObjectURL(new Blob([blob], { type: "text/html;charset=utf-8" })), "_blank");
  },
  downloadStatementCsv: async (pid: string, sid: string, name: string): Promise<void> => {
    saveBlob(await fetchBlob(`${BASE}/${pid}/statements/${sid}/csv`), `${name}.csv`);
  },
  downloadMonthCsv: async (month?: string): Promise<void> => {
    saveBlob(await fetchBlob(`${BASE}/_statements.csv${month ? `?month=${month}` : ""}`), `对账单汇总${month ? `_${month}` : ""}.csv`);
  },
};

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 统一的应收状态标签（逾期 N 天 / 未到期 / 部分收 / 已收 / 已作废） */
export function receivableBadge(r: ReceivableRow): { label: string; tone: "danger" | "muted" | "warning" | "success" | "info" } {
  if (r.status === "void") return { label: "已作废", tone: "muted" };
  if (r.status === "paid") return { label: "已收", tone: "success" };
  if (r.overdue) return { label: `逾期 ${r.overdue_days} 天`, tone: "danger" };
  if (r.status === "partial") return { label: "部分收", tone: "warning" };
  return { label: "未到期", tone: "muted" };
}

export const STATEMENT_STATUS_LABEL: Record<StatementStatus, string> = { draft: "未发送", sent: "已发送", paid: "已收齐", overdue: "逾期" };
